import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';

interface BottomNavItem {
  label: string;
  icon: string;
  link: string;
  queryParams?: Record<string, string>;
  exact?: boolean;
  /** Extra URL prefixes that should also highlight this tab. */
  matches?: string[];
}

/**
 * Persistent thumb-reach navigation on phones/tablets (hidden >= 992px, where
 * the header carries the same destinations). Orders/Account are guarded routes:
 * guests get the login sheet and are redirected back after signing in.
 */
@Component({
  selector: 'app-mobile-bottom-nav',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './mobile-bottom-nav.component.html',
  styleUrls: ['./mobile-bottom-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileBottomNavComponent {
  private router = inject(Router);

  readonly items: BottomNavItem[] = [
    { label: 'Home', icon: 'fa-house', link: '/', exact: true },
    { label: 'Categories', icon: 'fa-table-cells-large', link: '/categories', matches: ['/category/', '/products/'] },
    { label: 'Search', icon: 'fa-magnifying-glass', link: '/search' },
    { label: 'Orders', icon: 'fa-receipt', link: '/profile', queryParams: { tab: 'orders' }, matches: ['/order-summary'] },
    { label: 'Account', icon: 'fa-user', link: '/profile', queryParams: { tab: 'profile' } },
  ];

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  isActive(item: BottomNavItem): boolean {
    const url = this.url() || '/';
    const [path, query = ''] = url.split('?');

    if (item.exact) return path === item.link;
    if (item.matches?.some(prefix => path.startsWith(prefix))) return true;
    if (!path.startsWith(item.link)) return false;
    if (!item.queryParams) return true;

    // /profile serves several tabs - only highlight the tab that's open.
    const params = new URLSearchParams(query);
    const tab = params.get('tab') || 'profile';
    return item.queryParams['tab'] === 'orders' ? tab === 'orders' : tab !== 'orders';
  }
}
